import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Stepper,
  Step,
  StepLabel,
  Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Email as EmailIcon,
  CheckCircle as CheckIcon,
  AutoAwesome as SparkleIcon,
  Edit as ProposalIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { axiosService } from 'shared/helpers/axios';

// Number of bonus credits — keep in sync with backend FREE_SIGNUP_CREDITS
const BONUS_CREDITS = 2;

// Mini confetti particles for the celebration screen
const CONFETTI_COLORS = ['#FFD700', '#FF6B6B', '#4ECDC4', '#96CEB4', '#DDA0DD', '#45B7D1'];
const PARTICLES = Array.from({ length: 18 }, (_, i) => {
  const r = (n) => (((i * n * 2654435761) >>> 0) % 1000) / 1000;
  return {
    id: i,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    x: (r(3) - 0.5) * 340,
    y: -(r(7) * 180 + 40),
    rotate: (r(11) - 0.5) * 720,
    size: r(5) * 8 + 5,
    delay: r(13) * 0.35,
    isSquare: i % 3 !== 0,
  };
});

function ConfettiBurst() {
  return (
    <Box sx={{ position: 'absolute', top: '40%', left: '50%', pointerEvents: 'none', zIndex: 10 }}>
      {PARTICLES.map((p) => (
        <motion.div
          key={p.id}
          initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 1 }}
          animate={{ x: p.x, y: p.y, opacity: 0, rotate: p.rotate, scale: 0.3 }}
          transition={{ duration: 1.0, delay: p.delay, ease: [0.22, 1, 0.36, 1] }}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.isSquare ? p.size : p.size * 0.6,
            borderRadius: p.isSquare ? 2 : '50%',
            backgroundColor: p.color,
          }}
        />
      ))}
    </Box>
  );
}

const VerifyMembershipDialog = ({
  open,
  onClose,
  membership,
  onUploadDocument,
  onVerified,
}) => {
  const navigate = useNavigate();
  const [verificationMethod, setVerificationMethod] = useState('email'); // 'document' or 'email'
  const [document, setDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [, setIsDragging] = useState(false);
  
  // Email verification states
  const [emailStep, setEmailStep] = useState(0); // 0: enter email, 1: enter code
  const [officialEmail, setOfficialEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleFileSelect = (file) => {
    if (file) {
      // Validate file size (5MB limit - reasonable for A4 documents/ID cards/badges)
      const maxSizeMB = 5;
      if (file.size > maxSizeMB * 1024 * 1024) {
        setError(`File size must be less than ${maxSizeMB}MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        return;
      }
      
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF, JPG, PNG, DOC, or DOCX file');
        return;
      }
      
      setDocument(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!document) {
      setError('Please select a verification document');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      await onUploadDocument(membership.membership_id, document);
      setSuccess(true);
      // Auto-close after 2 seconds on success
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSendVerificationCode = async () => {
    if (!officialEmail) {
      setError('Please enter your official organization email');
      return;
    }

    setSendingCode(true);
    setError(null);

    try {
      await axiosService.post(
        `/consortium/organization-memberships/${membership.membership_id}/initiate-email-verification/`,
        { official_email: officialEmail }
      );
      setEmailStep(1);
      setSuccess(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send verification code. Please try again.');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setVerifying(true);
    setError(null);

    try {
      await axiosService.post(
        `/consortium/organization-memberships/${membership.membership_id}/verify-email-code/`,
        { verification_code: verificationCode }
      );
      setSuccess(true);
      onVerified?.();
      // Auto-close after 2 seconds on success
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired verification code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleClose = () => {
    setDocument(null);
    setError(null);
    setSuccess(false);
    setUploading(false);
    setIsDragging(false);
    setVerificationMethod('email');
    setEmailStep(0);
    setOfficialEmail('');
    setVerificationCode('');
    setSendingCode(false);
    setVerifying(false);
    onClose();
  };


  if (!membership) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Verify Membership</DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom sx={{ mb: 3 }}>
          Verify your membership in{' '}
          <strong>{membership.organization_name || membership.name}</strong>
        </Typography>

        {/* Verification Method Selector */}
        <Box sx={{ mb: 3 }}>
          {/* <Typography variant="subtitle2" gutterBottom>
            Choose Verification Method
          </Typography> */}
          <ToggleButtonGroup
            value={verificationMethod}
            exclusive
            onChange={(e, newMethod) => {
              if (newMethod) {
                setVerificationMethod(newMethod);
                setError(null);
                setSuccess(false);
              }
            }}
            fullWidth
            disabled={success || uploading || sendingCode || verifying}
          >
            <ToggleButton value="email">
              <EmailIcon sx={{ mr: 1 }} />
              Email Verification
            </ToggleButton>
            <ToggleButton value="document" disabled>
              <UploadIcon sx={{ mr: 1 }} />
              ID Upload (Coming Soon)
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Document Upload Method */}
        {/* {verificationMethod === 'document' && (
          <>
            <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 2 }}>
              Upload a document proving your membership (employment letter, ID card, badge, keycard, etc.). 
              Maximum file size: 5MB. An admin will review and verify your membership.
            </Typography>

            <input
              type="file"
              id="verification-file-input"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            <UploadArea
              isDragging={isDragging}
              onClick={() => document.getElementById('verification-file-input').click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              elevation={0}
            >
              <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" gutterBottom>
                Click to upload or drag and drop
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PDF, JPG, PNG, DOC, DOCX (max 5MB)
              </Typography>
            </UploadArea>

            {document && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DocumentIcon color="primary" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {document.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(document.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
                <Button size="small" onClick={() => setDocument(null)} disabled={uploading}>
                  Remove
                </Button>
              </Box>
            )}
          </>
        )} */}

        {/* Email Verification Method */}
        {verificationMethod === 'email' && (
          <>
            <Stepper activeStep={emailStep} sx={{ mb: 3 }}>
              <Step>
                <StepLabel>Enter Official Email</StepLabel>
              </Step>
              <Step>
                <StepLabel>Verify Code</StepLabel>
              </Step>
            </Stepper>

            {emailStep === 0 && (
              <>
                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 2 }}>
                  Enter your official organization email address. We'll send you a 6-digit verification code.
                </Typography>

                <TextField
                  fullWidth
                  label="Official Email Address"
                  type="email"
                  value={officialEmail}
                  onChange={(e) => setOfficialEmail(e.target.value)}
                  placeholder="your.name@organization.edu"
                  disabled={sendingCode}
                  helperText="Enter your organization email address"
                  sx={{ mb: 2 }}
                />
              </>
            )}

            {emailStep === 1 && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  A 6-digit verification code has been sent to <strong>{officialEmail}</strong>. 
                  The code will expire in 30 minutes.
                </Alert>

                <TextField
                  fullWidth
                  label="Verification Code"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(value);
                  }}
                  placeholder="123456"
                  disabled={verifying}
                  helperText="Enter the 6-digit code from your email"
                  inputProps={{
                    maxLength: 6,
                    style: { fontSize: '1.5rem', letterSpacing: '0.5rem', textAlign: 'center' }
                  }}
                  sx={{ mb: 2 }}
                />

                <Button
                  size="small"
                  onClick={() => {
                    setEmailStep(0);
                    setVerificationCode('');
                    setError(null);
                  }}
                  disabled={verifying}
                >
                  Change Email Address
                </Button>
              </>
            )}
          </>
        )}

        {/* Success — celebration panel */}
        {success && verificationMethod === 'email' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          >
            <Box sx={{ position: 'relative', textAlign: 'center', py: 3 }}>
              <ConfettiBurst />

              {/* Trophy emoji with pulse */}
              <motion.div
                animate={{ scale: [1, 1.18, 1] }}
                transition={{ duration: 0.55, delay: 0.15 }}
              >
                <Typography sx={{ fontSize: '3.5rem', lineHeight: 1, mb: 1 }}>🎉</Typography>
              </motion.div>

              <Typography variant="h6" fontWeight={700} color="success.main" gutterBottom>
                Membership Verified!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Your affiliation with{' '}
                <strong>{membership?.organization_name || membership?.name}</strong> is confirmed.
              </Typography>

              {/* Credit award box */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  background: 'linear-gradient(135deg, #fffbea 0%, #fef3c7 100%)',
                  border: '1px solid rgba(245,158,11,0.4)',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.75 }}>
                  <SparkleIcon sx={{ color: '#d97706', fontSize: '1.2rem' }} />
                  <Typography variant="body1" fontWeight={700} sx={{ color: '#92400e' }}>
                    You've received {BONUS_CREDITS} free AI credits!
                  </Typography>
                  <SparkleIcon sx={{ color: '#d97706', fontSize: '1.2rem' }} />
                </Box>
                <Typography variant="caption" sx={{ color: '#78350f', display: 'block' }}>
                  Use them with the Otable AI writing assistant to accelerate your research proposals.
                </Typography>
              </Paper>

              <Chip
                icon={<ProposalIcon sx={{ fontSize: '0.95rem !important' }} />}
                label="Open Proposal Writer to try it"
                color="primary"
                variant="outlined"
                clickable
                onClick={() => { handleClose(); navigate('/proposals/'); }}
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </motion.div>
        )}

        {/* Document upload success */}
        {success && verificationMethod === 'document' && (
          <Alert severity="success" sx={{ mt: 2 }} icon={<CheckIcon />}>
            Document uploaded successfully! An admin will review it and verify your membership.
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading || sendingCode || verifying}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        
        {!success && verificationMethod === 'document' && (
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!document || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload Document'}
          </Button>
        )}

        {!success && verificationMethod === 'email' && emailStep === 0 && (
          <Button
            onClick={handleSendVerificationCode}
            variant="contained"
            disabled={!officialEmail || sendingCode}
            startIcon={sendingCode ? <CircularProgress size={16} /> : <EmailIcon />}
          >
            {sendingCode ? 'Sending...' : 'Send Code'}
          </Button>
        )}

        {!success && verificationMethod === 'email' && emailStep === 1 && (
          <Button
            onClick={handleVerifyCode}
            variant="contained"
            disabled={verificationCode.length !== 6 || verifying}
            startIcon={verifying ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            {verifying ? 'Verifying...' : 'Verify Code'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VerifyMembershipDialog;
